import { 
  Package, 
  Users, 
  ShoppingCart, 
  ClipboardCheck, 
  Truck, 
  CheckCircle, 
  Receipt, 
  FileText,
  BarChart3,
  Settings,
  Shield,
  FolderOpen,
  HandHeart,
  History,
  Scale,
  FileX,
  User
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { PermissionWrapper } from "@/components/auth";
import { canAccessModule } from "@/utils/permissions";
import { useProcurement } from "@/context/ProcurementContext";

const menuItems = [
  { title: "Dashboard", url: "/", icon: BarChart3, module: "dashboard" },
  { title: "SKU Management", url: "/skus", icon: Package, module: "skus" },
  { title: "Vendor Management", url: "/vendors", icon: Users, module: "vendors" },
  { title: "Purchase Orders", url: "/purchase-orders", icon: ShoppingCart, module: "purchase-orders" },
  { title: "PO Management", url: "/po-management", icon: FolderOpen, module: "po-management" },
  { title: "Security Verification", url: "/security-verification", icon: Shield, module: "security-verification" },
  { title: "Invoices & Payments", url: "/invoices", icon: Receipt, module: "invoices" },
  { title: "Debit Notes", url: "/debit-notes", icon: FileX, module: "debit-notes" },
  { title: "Quality Control", url: "/quality", icon: CheckCircle, module: "quality" },
  { title: "Floor Manager Dispatch", url: "/floor-manager-dispatch", icon: HandHeart, module: "floor-manager-dispatch" },
  { title: "Quantity Testing", url: "/quantity-testing", icon: Scale, module: "quantity-testing" },
  { title: "Manager Section", url: "/manager-section", icon: User, module: "manager-section" },
  { title: "Handover Log", url: "/handover-log", icon: History, module: "handover-log" },
  { title: "Vendor Invoices", url: "/vendor-invoices", icon: FileText, module: "vendor-invoices" },
];

const adminItems = [
  { title: "User Management", url: "/users", icon: Shield, module: "users" },
  { title: "System Settings", url: "/settings", icon: Settings, module: "settings" },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  
  // Safe context access with error handling
  let procurementState;
  try {
    const context = useProcurement();
    procurementState = context.state;
  } catch (error) {
    console.error('Context error in AppSidebar:', error);
    // Fallback state
    procurementState = { currentUser: null };
  }
  const isCollapsed = state === "collapsed";

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const getNavClasses = (path: string) => {
    const active = isActive(path);
    return active 
      ? "bg-primary/10 text-primary border-r-2 border-primary font-medium" 
      : "text-muted-foreground hover:text-foreground hover:bg-secondary/50";
  };

  return (
    <Sidebar className="bg-surface border-r border-card-border" collapsible="icon">
      <SidebarContent className="p-2">
        <SidebarGroup>
          <SidebarGroupLabel className={`${isCollapsed ? "sr-only" : ""} text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2`}>
            Main Modules
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {menuItems.map((item) => {
                const userRole = procurementState.currentUser?.role;
                const hasAccess = !userRole || canAccessModule(userRole as any, item.module);
                
                if (!hasAccess) return null;
                
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild className="h-10" tooltip={isCollapsed ? item.title : undefined}>
                      <NavLink 
                        to={item.url} 
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${getNavClasses(item.url)}`}
                      >
                        <item.icon className="h-5 w-5 flex-shrink-0" />
                        {!isCollapsed && <span className="font-medium">{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup className="mt-8">
          <SidebarGroupLabel className={`${isCollapsed ? "sr-only" : ""} text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2`}>
            Administration
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {adminItems.map((item) => {
                const userRole = procurementState.currentUser?.role;
                const hasAccess = !userRole || canAccessModule(userRole as any, item.module);
                
                if (!hasAccess) return null;
                
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild className="h-10" tooltip={isCollapsed ? item.title : undefined}>
                      <NavLink 
                        to={item.url} 
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${getNavClasses(item.url)}`}
                      >
                        <item.icon className="h-5 w-5 flex-shrink-0" />
                        {!isCollapsed && <span className="font-medium">{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}